import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Instagram, ArrowUpRight, ChevronLeft, ChevronRight, Heart, Layers, Play } from "lucide-react";
import { SectionHead, Reveal } from "./SectionHead";
import { api, asset } from "@/lib/api";

const HANDLE = "ghoul_ex";
const PROFILE = `https://www.instagram.com/${HANDLE}/`;
const INTERVAL = 4500;

const useFeed = () => {
  const [feed, setFeed] = useState(null);
  useEffect(() => {
    api.get("/instagram").then((r) => setFeed(r.data)).catch(() => setFeed({ posts: [] }));
  }, []);
  return feed;
};

const ProfileCard = ({ profile }) => (
  <div className="flex items-center gap-4">
    {profile?.avatar && <img src={asset(profile.avatar)} alt={profile.username} className="w-16 h-16 rounded-full border-2 border-[#F5A524] object-cover" />}
    <div>
      <p data-testid="latest-profile-name" className="font-display font-black uppercase text-2xl leading-none">{profile?.full_name || `@${HANDLE}`}</p>
      <p className="mt-1 font-mono text-[11px] tracking-[0.2em] text-zinc-400">
        @{profile?.username || HANDLE}
        {profile?.followers != null && <> · <span className="text-[#F5A524]">{profile.followers}</span> FOLLOWERS</>}
        {profile?.posts != null && <> · <span className="text-[#F5A524]">{profile.posts}</span> POSTS</>}
      </p>
    </div>
  </div>
);

const Slide = ({ post }) => (
  <motion.a key={post.id} href={`https://www.instagram.com/p/${post.shortcode}/`} target="_blank" rel="noreferrer" data-testid={`latest-slide-${post.id}`}
    className="col-start-1 row-start-1 block relative group"
    initial={{ opacity: 0, scale: 1.06, filter: "brightness(2) saturate(0)" }}
    animate={{ opacity: 1, scale: 1, filter: "brightness(1) saturate(1)" }}
    exit={{ opacity: 0, scale: 0.98 }}
    transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
    <img src={asset(post.image)} alt={post.caption?.slice(0, 80) || "Instagram post"} className="w-full aspect-square object-cover" />
    <span className="absolute top-4 right-4 bg-black/75 text-[#F5A524] p-2">{post.is_video ? <Play size={16} /> : post.is_carousel ? <Layers size={16} /> : <Instagram size={16} />}</span>
    {post.pinned && <span data-testid="latest-pinned-badge" className="absolute top-4 left-4 bg-[#F5A524] text-black font-mono text-[11px] font-bold tracking-[0.2em] px-2 py-1">PINNED</span>}
    <span className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black via-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
      <span className="flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] text-[#F5A524]">OPEN ON INSTAGRAM <ArrowUpRight size={14} /></span>
    </span>
  </motion.a>
);

export const Latest = ({ index }) => {
  const feed = useFeed();
  const posts = Array.isArray(feed?.posts) ? feed.posts : [];
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = posts.length;
  const post = n ? posts[i % n] : null;

  useEffect(() => {
    if (n < 2 || paused) return undefined;
    const t = setTimeout(() => setI((x) => (x + 1) % n), INTERVAL);
    return () => clearTimeout(t);
  }, [i, n, paused]);

  const go = (d) => setI((x) => (x + d + n) % n);

  return (
    <section id="latest" data-testid="latest-section" className="relative px-5 sm:px-10 lg:px-16 py-16 md:py-24 border-t border-[#F5A524]/15">
      <SectionHead index={index} kicker="LATEST" title={<>Fresh off<br /><span className="text-[#F5A524]">the wall</span></>}>
        <a data-testid="latest-follow-btn" href={PROFILE} target="_blank" rel="noreferrer"
          className="group clip-tab inline-flex items-center gap-3 bg-[#F5A524] text-black font-mono font-bold text-sm tracking-[0.2em] uppercase px-6 py-4 hover:bg-white transition-colors">
          <Instagram size={18} /> @{HANDLE} <ArrowUpRight size={16} className="transition-transform group-hover:rotate-45" />
        </a>
      </SectionHead>

      {feed && n === 0 && (
        <p data-testid="latest-empty" className="font-mono text-sm text-zinc-400 border border-dashed border-[#F5A524]/30 p-10">FEED OFFLINE — CATCH THE LATEST ON INSTAGRAM @{HANDLE}.</p>
      )}
      {!feed && <p className="font-mono text-sm text-zinc-400">SYNCING FEED<span className="blink">_</span></p>}

      {post && (
        <Reveal>
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-start" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
            <div className="lg:col-span-5 order-2 lg:order-1 space-y-5 lg:space-y-8">
              <ProfileCard profile={feed.profile} />
              <div className="border-t border-[#F5A524]/20 pt-4 lg:pt-6 lg:min-h-[150px]">
                <p className="font-mono text-[11px] tracking-[0.3em] text-zinc-400 mb-3">POST {String((i % n) + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
                  {post.likes != null && <span className="ml-3 inline-flex items-center gap-1 text-[#FF2A55]"><Heart size={11} className="fill-current" /> {post.likes}</span>}
                </p>
                <AnimatePresence initial={false}>
                  <motion.p key={post.id} data-testid="latest-caption" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                    className="text-base sm:text-lg text-zinc-300 leading-relaxed line-clamp-2 lg:line-clamp-4">{post.caption || "—"}</motion.p>
                </AnimatePresence>
              </div>
              <div className="flex items-center gap-3">
                <button data-testid="latest-prev-btn" onClick={() => go(-1)} aria-label="Previous post" className="w-11 h-11 grid place-items-center border border-[#F5A524]/40 text-[#F5A524] hover:bg-[#F5A524] hover:text-black transition-colors"><ChevronLeft size={18} /></button>
                <button data-testid="latest-next-btn" onClick={() => go(1)} aria-label="Next post" className="w-11 h-11 grid place-items-center border border-[#F5A524]/40 text-[#F5A524] hover:bg-[#F5A524] hover:text-black transition-colors"><ChevronRight size={18} /></button>
                <div className="flex-1 flex gap-1.5">
                  {posts.map((p, k) => (
                    <button key={p.id} data-testid={`latest-dot-${k}`} onClick={() => setI(k)} aria-label={`Post ${k + 1}`} className="flex-1 h-1 bg-white/15 overflow-hidden">
                      {k === i % n && <motion.span key={`${i}-${paused}`} className="block h-full bg-[#F5A524] origin-left" initial={{ scaleX: paused ? 1 : 0 }} animate={{ scaleX: 1 }} transition={{ duration: paused ? 0 : INTERVAL / 1000, ease: "linear" }} />}
                      {k < i % n && <span className="block h-full bg-[#F5A524]/50" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <div className="clip-corner bg-[#F5A524]/40 p-px max-w-[540px] lg:ml-auto">
                <div className="clip-corner bg-[#12131A] overflow-hidden grid relative">
                  <AnimatePresence initial={false}>
                    <Slide post={post} />
                  </AnimatePresence>
                </div>
              </div>
              <div data-lenis-prevent className="mt-4 hidden md:flex gap-2 overflow-x-auto max-w-[540px] lg:ml-auto pb-1">
                {posts.map((p, k) => (
                  <button key={p.id} data-testid={`latest-thumb-${k}`} onClick={() => setI(k)} aria-label={`Show post ${k + 1}`}
                    className={`relative shrink-0 w-[calc((100%-40px)/6)] min-w-[52px] aspect-square overflow-hidden border transition-[border-color,opacity] ${k === i % n ? "border-[#F5A524] opacity-100" : "border-transparent opacity-50 hover:opacity-100"}`}>
                    <img src={asset(p.image)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      )}
    </section>
  );
};
